/** ============================================================
 * api.js — Wrapper de fetch hacia los endpoints n8n del Back Office
 * Todas las peticiones incluyen el JWT de sesión automáticamente
 * ============================================================ */

const BO_API = {
    BASE: 'https://n8n.duletias3rver.com/webhook',

    _headers() {
        const token = sessionStorage.getItem('bo_token') || localStorage.getItem('bo_token');
        return {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        };
    },

    async _request(method, path, body = null) {
        const url = `${this.BASE}${path}`;
        const opts = { method, headers: this._headers() };
        if (body) opts.body = JSON.stringify(body);

        const res = await fetch(url, opts);

        if (res.status === 401) {
            if (typeof BO_AUTH !== 'undefined' && BO_AUTH.logout) BO_AUTH.logout();
            throw new Error('Tu sesión ha expirado. Por favor inicia sesión nuevamente.');
        }
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            const errMsg = err.message || err.error || `Error ${res.status}`;
            if (res.status === 403 && /token expired|jwt expired|sesión expirada|invalid signature/i.test(errMsg)) {
                if (typeof BO_AUTH !== 'undefined' && BO_AUTH.showAlert) {
                    BO_AUTH.showAlert('Tu sesión ha expirado. Por favor inicia sesión de nuevo.', 'warning');
                }
                setTimeout(() => { if (typeof BO_AUTH !== 'undefined' && BO_AUTH.logout) BO_AUTH.logout(); }, 1500);
                throw new Error('Tu sesión ha expirado. Por favor inicia sesión nuevamente.');
            }
            throw new Error(errMsg);
        }
        const text = await res.text();
        let parsed = {};
        try {
            parsed = text ? JSON.parse(text) : {};
        } catch(e) {
            parsed = { raw: text };
        }
        
        // Detectar soft-errors de n8n o pg (cuando devuelven 200 OK pero con mensaje de error SQL)
        if (parsed.ok === false) {
            throw new Error(parsed.message || parsed.error || 'Error en la petición del servidor');
        }
        if (parsed.message && parsed.message.toLowerCase().includes('relation') && parsed.message.toLowerCase().includes('does not exist')) {
            throw new Error('La tabla en base de datos no existe: ' + parsed.message);
        }
        if (parsed.error || (parsed.message && parsed.message.toLowerCase().includes('error'))) {
            throw new Error(parsed.error || parsed.message);
        }
        return parsed;
    },

    get:    (path)       => BO_API._request('GET',    path),
    post:   (path, b)    => BO_API._request('POST',   path, b),
    put:    (path, b)    => BO_API._request('PUT',    path, b),
    delete: (path)       => BO_API._request('DELETE', path),

    // ============================================================
    // ENDPOINTS REALES — todos apuntan al n8n interno
    // ============================================================

    // — Auth —
    login:  (email, pass) => BO_API.post('/bo/login',  { email, password: pass }),
    me:     ()            => BO_API.get('/bo/me'),

    // — Usuarios / Árbol / Espejo —
    getTree:    (id)    => BO_API.post('/bo/users/tree', { id: Number(id), registro: Number(id) }),
    getUser:    (id)    => BO_API.get(`/bo/users/${id}`),
    listUsers:  (p)     => BO_API.post('/bo/users/list', p || {}),
    createUser: (data)  => BO_API.post('/bo/users/create', data),
    mirrorUser: (id)    => BO_API.get(`/bo/users/mirror/${id}`),
    updateUser: (id, d) => BO_API.put(`/bo/users/${id}`, d),

    // — Dashboard KPIs —
    getStats: () => BO_API.get('/bo/stats'),

    // — Rescate & Reactivación Comercial (vía Materialized View optimizada) —
    getRescate: (limit = 2000) => BO_API.post('/bo/sql-relay', {
        secret: 'duletia_relay_2026',
        sql: `SELECT * FROM mv_rescate_empresarial ORDER BY dias_sin_compra ASC LIMIT ${limit}`
    }).then(r => r.rows || r),

    getRescateKpis: () => BO_API.post('/bo/sql-relay', {
        secret: 'duletia_relay_2026',
        sql: `SELECT 
            COUNT(*) as total_recuperables,
            COUNT(*) FILTER (WHERE estatus_riesgo = 'riesgo_fresco') as riesgo_fresco,
            COUNT(*) FILTER (WHERE estatus_riesgo = 'peligro_critico') as peligro_critico,
            COUNT(*) FILTER (WHERE estatus_riesgo = 'baja_reciente') as baja_reciente,
            COUNT(*) FILTER (WHERE tiene_red_activa = true) as con_red_activa,
            COUNT(*) FILTER (WHERE total_frontales > 0) as con_red_total,
            COUNT(*) FILTER (WHERE galardon IS NOT NULL AND galardon != '') as led_en_riesgo,
            COALESCE(SUM(cajas_red_60d) FILTER (WHERE tiene_red_activa = true), 0) as cajas_red_esperando,
            COALESCE(SUM(total_cajas), 0) as total_cajas_historicas
        FROM mv_rescate_empresarial`
    }).then(r => r.rows?.[0] || r?.[0] || {}),

    refreshRescate: () => BO_API.post('/bo/sql-relay', {
        secret: 'duletia_relay_2026',
        sql: `REFRESH MATERIALIZED VIEW CONCURRENTLY mv_rescate_empresarial;`
    }),

    getSeguimientoCounts: () => BO_API.post('/bo/sql-relay', {
        secret: 'duletia_relay_2026',
        sql: `SELECT cliente_id, COUNT(*) AS num_seguimientos, MAX(created_at) AS ultimo_seguimiento FROM rescate_seguimiento GROUP BY cliente_id`
    }).then(r => r.rows || r).catch(() => []),
    assignRescate: (payload) => BO_API.post('/bo/rescate/assign', payload),

    // — Activos —
    getActivos:    (uid, mes) => BO_API.get(`/bo/activos/${uid}/${mes}`),
    getActivosMes: (mes)      => BO_API.get(`/bo/activos/resumen/${mes}`),

    // — Ventas —
    listVentas:    (p)    => BO_API.post('/bo/ventas/list',   p || {}),
    createVenta:   (data) => BO_API.post('/bo/ventas/create', data),
    aprobarVenta:  (id, data)  => BO_API.post(`/bo/ventas/aprobar/${id}`, data),

    // — Inventario —
    getInventario: (cedisId) => BO_API.get(`/bo/inventario/${cedisId}`),

    // — Premios / Galardones (via webhooks n8n — incluyen notificación automática al líder) —
    getPremios:    (uid)  => BO_API.get('/bo/premios-especiales' + (uid ? `?userId=${uid}` : '')),
    asignarPremio: (data) => BO_API.post('/bo/premios-especiales/asignar', data),

    // — Configuración —
    getConfig:           () => BO_API.get('/bo/config'),
    savePrecios:       (d)  => BO_API.post('/bo/config/precios',      d),
    saveDistribucion:  (d)  => BO_API.post('/bo/config/distribucion', d),

    // — Facturas / CFDI —
    listFacturas:  (p)    => BO_API.post('/bo/facturas/list',    p || {}),
    crearFactura:  (data) => BO_API.post('/bo/facturas/create',  data),
    cancelarFactura: (id) => BO_API.post(`/bo/facturas/cancel/${id}`, {}),

    // — Rescates —
    listRescates:   (p)   => BO_API.post('/bo/rescate/list',    p || {}),
    crearRescate:   (d)   => BO_API.post('/bo/rescate/create',  d),
    aprobarRescate: (id)  => BO_API.post(`/bo/rescate/aprobar/${id}`, {}),

    // — Cartera vencida —
    getCarteraVencida: (p) => BO_API.post('/bo/cartera/list', p || {}),

    // — Notificaciones —
    getNotificaciones:  ()    => BO_API.get('/bo/notificaciones'),
    marcarLeida:        (id)  => BO_API.post(`/bo/notificaciones/${id}/leida`, {}),

    // — Academia —
    getAcademia:       (uid)  => BO_API.get(`/bo/academia/${uid}`),
    marcarModulo:      (data) => BO_API.post('/bo/academia/progreso', data),

    // — Fase 4: Plataforma de Aceleración —
    getCalendario:     ()     => BO_API.get('/bo/fase4/calendario'),
    crearCampana:      (data) => BO_API.post('/bo/fase4/calendario', data),
    deleteCampana:     (id)   => BO_API.delete(`/bo/fase4/calendario?id=${id}`),
    getAcelerador:     (uid)  => BO_API.get(`/bo/fase4/acelerador?uid=${uid}`),
    getAceleradorMetricas: (projectId, liderId, fInicio, fFin) => BO_API.get(`/bo/fase4/acelerador/metricas?projectId=${projectId}&liderId=${liderId}&fInicio=${fInicio}&fFin=${fFin}`),
    updateAceleradorChecklist: (projectId, checklist) => BO_API.put(`/bo/fase4/acelerador/checklist`, { projectId, checklist }),
    saveAcelerador:    (data) => BO_API.post('/bo/fase4/acelerador', data),
    deleteAcelerador:  (projectId) => BO_API.delete(`/bo/fase4/acelerador?projectId=${projectId}`),
    getBitacora:       (uid, mes) => BO_API.get(`/bo/fase4/bitacora?uid=${uid}&mes=${mes || '2026-04'}`),
    saveBitacoraAuditoria: (uid, score, mes, semana) => BO_API.post(`/bo/fase4/bitacora/auditoria`, { uid, score, mes: mes || '2026-04', semana: semana || 1 }),
    deleteBitacoraEvidencia: (id) => BO_API.delete(`/bo/fase4/bitacora/evidencia?id=${id}`),

    // ── Reportes de Inventario ────────────────────────────────────
    // Reporte tipo CONTPAQi: existencia inicial + entradas + salidas = existencia final
    getReporteInventario: (desde, hasta, almacen) => BO_API.post('/bo/reportes/inventario', { desde, hasta, almacen: almacen || '' }),
    getReporteMermas:     (desde, hasta, almacen) => BO_API.post('/bo/reportes/mermas',     { desde, hasta, almacen: almacen || '' }),
    getReporteTraspasos:  (desde, hasta, almacen) => BO_API.post('/bo/reportes/traspasos',  { desde, hasta, almacen: almacen || '' }),

    // ── Producción Diaria ─────────────────────────────────────────
    listProduccion:    (p)    => BO_API.post('/bo/produccion/list',   p || {}),
    crearProduccion:   (data) => BO_API.post('/bo/produccion/create', data),
    getProduccion:     (id)   => BO_API.get(`/bo/produccion/${id}`),
    deleteProduccion:  (id)   => BO_API.delete(`/bo/produccion/${id}`),

    // ── Costos y Finanzas ─────────────────────────────────────────
    listCostosInventario:  (mes)  => BO_API.get(`/bo/costos/inventario?mes=${mes}`),
    crearCostoInventario:  (data) => BO_API.post('/bo/costos/inventario', data),
    listGastosMensuales:   (mes)  => BO_API.get(`/bo/costos/gastos?mes=${mes}`),
    crearGasto:            (data) => BO_API.post('/bo/costos/gastos', data),
    deleteGasto:           (id)   => BO_API.delete(`/bo/costos/gastos/${id}`),
    getPnL:                (mes)  => BO_API.get(`/bo/costos/pnl?mes=${mes}`),
    getPnLAnual:           (anio) => BO_API.get(`/bo/costos/pnl/anual?anio=${anio}`),
    // ============================================================
    // MOCK DATA — fallback cuando n8n no está disponible
    // ============================================================
    MOCK: {
        stats: {
            total_lideres:             847,
            activos_mes:               514,
            nuevos_mes:                38,
            en_riesgo:                 23,
            baja_definitiva:           12,
            activos_distribuidos_mes:  2347800,
            ventas_mes:                2312500,
            // Ventas por tipo de pago (demo)
            ventas_efectivo:           55000,
            ventas_transferencia:      72000,
            ventas_deposito:           38000,
            ventas_activos:            22500,
            // Producto más vendido (demo)
            producto_top:              'Dulet Fresa',
            producto_top_cajas:        14,
            // Top CEDIS (demo)
            cedis_top:                 'Cancún, Q. Roo',
            cedis_top_monto:           45000,
        },
        usuarios: [
            { id: 1001, nombre: 'Ana',       apellido: 'García',     apellido2: 'Mendoza',  rol: 'cliente',   estatus: 'blanco',   frontales: 7,  galardon: 'acero',  pais: 'MEX', estado_aprobacion:'aprobado', ultimo_cal: '2026-03', email:'ana.garcia@mail.com',   celular:'3311001001', whatsapp:'3311001001', rfc:'GAMA850101XXX', patrocinador_id: null, calle:'Av. Chapultepec', noExterior:'120', colonia:'Americana', municipio:'Guadalajara', estado:'Jalisco', codigoPostal:'44100', contrato:'si', activos_disponibles: 4500 },
            { id: 1002, nombre: 'Luis',      apellido: 'Martínez',   apellido2: 'Ruiz',     rol: 'cliente',   estatus: 'gris',     frontales: 3,  galardon: null,     pais: 'MEX', estado_aprobacion:'aprobado', ultimo_cal: '2026-02', email:'luis.mtz@mail.com',    celular:'3312002002', patrocinador_id: 1001, contrato:'si', activos_disponibles: 900 },
            { id: 1003, nombre: 'María',     apellido: 'López',      apellido2: 'Vega',     rol: 'cliente',   estatus: 'amarillo', frontales: 2,  galardon: null,     pais: 'USA', estado_aprobacion:'aprobado', ultimo_cal: '2025-12', email:'maria.lopez@mail.com',  celular:'5550003003', patrocinador_id: 1001, contrato:'si', activos_disponibles: 0 },
            { id: 1004, nombre: 'Carlos',    apellido: 'Pérez',      apellido2: '',         rol: 'prospecto', estatus: 'nuevo',    frontales: 0,  galardon: null,     pais: 'MEX', estado_aprobacion:'pendiente',ultimo_cal: null,      email:'carlos.p@mail.com',    celular:'3314004004', patrocinador_id: 1002, contrato:'no', activos_disponibles: 0 },
            { id: 1005, nombre: 'Elena',     apellido: 'Torres',     apellido2: 'Bautista', rol: 'cliente',   estatus: 'blanco',   frontales: 11, galardon: 'plata',  pais: 'GTQ', estado_aprobacion:'aprobado', ultimo_cal: '2026-03', email:'elena.t@mail.com',      celular:'50222005005', patrocinador_id: 1001, contrato:'si', activos_disponibles: 13500 },
            { id: 1006, nombre: 'Roberto',   apellido: 'Hernández',  apellido2: 'Cruz',     rol: 'cliente',   estatus: 'blanco',   frontales: 5,  galardon: 'acero',  pais: 'MEX', estado_aprobacion:'aprobado', ultimo_cal: '2026-03', email:'roberto.h@mail.com',   celular:'3316006006', patrocinador_id: 1001, contrato:'si', activos_disponibles: 4500 },
            { id: 1007, nombre: 'Patricia',  apellido: 'Ramírez',    apellido2: 'Soto',     rol: 'cliente',   estatus: 'gris',     frontales: 2,  galardon: null,     pais: 'MEX', estado_aprobacion:'aprobado', ultimo_cal: '2026-01', email:'patricia.r@mail.com',  celular:'3317007007', patrocinador_id: 1002, contrato:'si', activos_disponibles: 0 },
            { id: 1008, nombre: 'Miguel',    apellido: 'Flores',     apellido2: '',         rol: 'prospecto', estatus: 'nuevo',    frontales: 0,  galardon: null,     pais: 'MEX', estado_aprobacion:'pendiente',ultimo_cal: null,      email:'miguel.f@mail.com',    celular:'3318008008', patrocinador_id: 1003, contrato:'no', activos_disponibles: 0 },
            { id: 1009, nombre: 'Sofía',     apellido: 'Morales',    apellido2: 'Peña',     rol: 'cliente',   estatus: 'blanco',   frontales: 8,  galardon: 'acero',  pais: 'MEX', estado_aprobacion:'aprobado', ultimo_cal: '2026-03', email:'sofia.m@mail.com',     celular:'3319009009', patrocinador_id: 1005, contrato:'si', activos_disponibles: 4500 },
            { id: 1010, nombre: 'Jorge',     apellido: 'Castillo',   apellido2: 'Luna',     rol: 'cliente',   estatus: 'amarillo', frontales: 1,  galardon: null,     pais: 'MEX', estado_aprobacion:'aprobado', ultimo_cal: '2025-11', email:'jorge.c@mail.com',     celular:'3310010010', patrocinador_id: 1005, contrato:'si', activos_disponibles: 0 },
            { id: 1011, nombre: 'Verónica',  apellido: 'Jiménez',    apellido2: 'Rios',     rol: 'cliente',   estatus: 'blanco',   frontales: 6,  galardon: 'acero',  pais: 'USA', estado_aprobacion:'aprobado', ultimo_cal: '2026-03', email:'veronica.j@mail.com',  celular:'3311011011', patrocinador_id: 1006, contrato:'si', activos_disponibles: 5400 },
            { id: 1012, nombre: 'Fernando',  apellido: 'Gutiérrez',  apellido2: '',         rol: 'cliente',   estatus: 'gris',     frontales: 0,  galardon: null,     pais: 'MEX', estado_aprobacion:'aprobado', ultimo_cal: '2025-10', email:'fernando.g@mail.com',  celular:'3312012012', patrocinador_id: 1006, contrato:'si', activos_disponibles: 0 },
            { id: 1013, nombre: 'Isabella',  apellido: 'Reyes',      apellido2: 'Montoya',  rol: 'cliente',   estatus: 'blanco',   frontales: 4,  galardon: null,     pais: 'GTQ', estado_aprobacion:'aprobado', ultimo_cal: '2026-02', email:'isabella.r@mail.com',  celular:'50222013013', patrocinador_id: 1009, contrato:'si', activos_disponibles: 0 },
            { id: 1014, nombre: 'Alejandro', apellido: 'Vargas',     apellido2: 'Delgado',  rol: 'prospecto', estatus: 'nuevo',    frontales: 0,  galardon: null,     pais: 'MEX', estado_aprobacion:'pendiente',ultimo_cal: null,      email:'alejandro.v@mail.com', celular:'3314014014', patrocinador_id: 1009, contrato:'no', activos_disponibles: 0 },
            { id: 1015, nombre: 'Daniela',   apellido: 'Mendoza',    apellido2: 'Aguilar',  rol: 'cliente',   estatus: 'blanco',   frontales: 9,  galardon: 'acero',  pais: 'MEX', estado_aprobacion:'aprobado', ultimo_cal: '2026-03', email:'daniela.m@mail.com',   celular:'3315015015', patrocinador_id: null, contrato:'si', activos_disponibles: 8100 },
            // ── Canadá 🇨🇦 ─────────────────────────────────────────────
            { id: 1016, nombre: 'Emma',      apellido: 'Tremblay',   apellido2: '',         rol: 'cliente',   estatus: 'blanco',   frontales: 6,  galardon: 'acero',  pais: 'CAN', estado_aprobacion:'aprobado', ultimo_cal: '2026-03', email:'emma.t@mail.ca',       celular:'+14161016016', patrocinador_id: 1001, contrato:'si', activos_disponibles: 0 },
            { id: 1017, nombre: 'Liam',      apellido: 'Roy',        apellido2: '',         rol: 'cliente',   estatus: 'gris',     frontales: 3,  galardon: null,     pais: 'CAN', estado_aprobacion:'aprobado', ultimo_cal: '2026-02', email:'liam.roy@mail.ca',     celular:'+14161017017', patrocinador_id: 1016, contrato:'si', activos_disponibles: 0 },
            { id: 1018, nombre: 'Olivia',    apellido: 'Gagnon',     apellido2: '',         rol: 'prospecto', estatus: 'nuevo',    frontales: 0,  galardon: null,     pais: 'CAN', estado_aprobacion:'pendiente',ultimo_cal: null,      email:'olivia.g@mail.ca',    celular:'+14161018018', patrocinador_id: 1016, contrato:'no', activos_disponibles: 0 },
            // ── El Salvador 🇸🇻 ─────────────────────────────────────────
            { id: 1019, nombre: 'Gabriela',  apellido: 'Melara',     apellido2: 'Rivas',    rol: 'cliente',   estatus: 'blanco',   frontales: 5,  galardon: 'acero',  pais: 'SLV', estado_aprobacion:'aprobado', ultimo_cal: '2026-03', email:'gabriela.m@mail.sv',  celular:'+5031019019', patrocinador_id: 1001, contrato:'si', activos_disponibles: 0 },
            { id: 1020, nombre: 'Rodrigo',   apellido: 'Cisneros',   apellido2: 'Amaya',    rol: 'cliente',   estatus: 'amarillo', frontales: 2,  galardon: null,     pais: 'SLV', estado_aprobacion:'aprobado', ultimo_cal: '2026-02', email:'rodrigo.c@mail.sv',   celular:'+5031020020', patrocinador_id: 1019, contrato:'si', activos_disponibles: 0 },
        ]

        // ── BD simulada de compras de cajas ────────────────────────────
        // Cada registro = una compra de caja completa que GENERA comisión
        // Formato: { usuario_id, mes:'YYYY-MM', cajas, tipo:'completa'|'media'x2 }
        ,compras: [
            // ── Marzo 2026 ──────────────────────────────────────────────
            // Ana García #1001 (N1 de nadie — es raíz)
            { usuario_id: 1001, mes: '2026-03', cajas: 1 },   // compra propia

            // Frontales directos de Ana García (N1 de 1001)
            { usuario_id: 1002, mes: '2026-03', cajas: 2 },   // Luis Martínez → Ana gana N1×2
            { usuario_id: 1005, mes: '2026-03', cajas: 1 },   // Elena Torres  → Ana gana N1×1
            { usuario_id: 1006, mes: '2026-03', cajas: 3 },   // Roberto Hernández → N1×3
            { usuario_id: 1003, mes: '2026-03', cajas: 0 },   // María López — no compró este mes

            // N2 de Ana (frontales de sus frontales)
            { usuario_id: 1004, mes: '2026-03', cajas: 0 },   // Carlos Pérez (hijo de Luis) — no compró
            { usuario_id: 1007, mes: '2026-03', cajas: 1 },   // Patricia Ramírez (hija de Luis) → Ana gana N2×1
            { usuario_id: 1009, mes: '2026-03', cajas: 2 },   // Sofía Morales (hija de Elena) → Ana gana N2×2
            { usuario_id: 1010, mes: '2026-03', cajas: 1 },   // Jorge Castillo (hijo de Elena) → Ana gana N2×1
            { usuario_id: 1011, mes: '2026-03', cajas: 1 },   // Verónica Jiménez (hija de Roberto) → N2×1
            { usuario_id: 1012, mes: '2026-03', cajas: 0 },   // Fernando Gutiérrez (hijo de Roberto) — no compró

            // N3 de Ana (hijos de sus N2)
            { usuario_id: 1013, mes: '2026-03', cajas: 2 },   // Isabella Reyes (hija de Sofía) → Ana gana N3×2
            { usuario_id: 1014, mes: '2026-03', cajas: 0 },   // Alejandro Vargas — no compró

            // N4 de Ana (hijos de sus N3)
            { usuario_id: 1015, mes: '2026-03', cajas: 1 },   // Daniela Mendoza (hija de Isabella) → Ana gana N4×1

            // ── Canadá / El Salvador — Marzo 2026 ────────────────────────
            { usuario_id: 1016, mes: '2026-03', cajas: 1 },  // Emma (CAN-N1 de Ana)
            { usuario_id: 1017, mes: '2026-03', cajas: 1 },  // Liam (CAN-N2)
            { usuario_id: 1019, mes: '2026-03', cajas: 1 },  // Gabriela (SLV-N1 de Ana)
            { usuario_id: 1020, mes: '2026-03', cajas: 1 },  // Rodrigo (SLV-N2)

            // ── Febrero 2026 ────────────────────────────────────────────
            { usuario_id: 1001, mes: '2026-02', cajas: 1 },
            { usuario_id: 1002, mes: '2026-02', cajas: 1 },
            { usuario_id: 1005, mes: '2026-02', cajas: 1 },
            { usuario_id: 1006, mes: '2026-02', cajas: 2 },
            { usuario_id: 1009, mes: '2026-02', cajas: 1 },
            { usuario_id: 1011, mes: '2026-02', cajas: 1 },
            { usuario_id: 1013, mes: '2026-02', cajas: 1 },
        ]
    }
};

// ══════════════════════════════════════════════════════════════
// MOTOR DE COMISIONES — cálculo desde la BD simulada
// Uso: _getComisionesPorNivel(titularId, mes)
// Retorna: { por_nivel[4], detalle[{nivel,nombre,id,cajas,comision}],
//            monto_generado, monto_gastado, monto_disponible }
// ══════════════════════════════════════════════════════════════
function _getComisionesPorNivel(titularId, mes) {
    const usuarios  = BO_API.MOCK.usuarios;
    const compras   = BO_API.MOCK.compras;
    const pcfg      = (typeof COMMISSION_CONFIG !== 'undefined') ? COMMISSION_CONFIG
                    : { precio_caja: { MXN:4500, USD:300, GTQ:2250 }, pct: { n1:20, n2:30, n3:5, n4:5 } };

    const titular = usuarios.find(u => u.id === titularId);
    if (!titular) return null;

    const moneda   = (typeof _getMoneda !== 'undefined') ? _getMoneda(titular) : { codigo: 'MXN' };
    const pBox     = pcfg.precio_caja[moneda.codigo] || 4500;
    const pct      = pcfg.pct;

    // Construir mapa id→usuario para acceso O(1)
    const uMap = {};
    usuarios.forEach(u => { uMap[u.id] = u; });

    // Obtener compras del mes (memoize con flag de uso de activos)
    const comprasMap = {};   // usuario_id → { cajas, usoActivos: boolean }
    compras.filter(c => c.mes === mes).forEach(c => { 
        if (!comprasMap[c.usuario_id]) comprasMap[c.usuario_id] = { cajas: 0, usoActivos: false };
        comprasMap[c.usuario_id].cajas += c.cajas;
        if (c.tipo_pago === 'activos' || c.activos_usados > 0) {
            comprasMap[c.usuario_id].usoActivos = true;
        }
    });

    // BFS por niveles desde el titular (sin incluirlo)
    const visited = new Set([titularId]);
    let fronteraNivel = [titularId];  // IDs a expandir en el próximo nivel
    const porNivelMonto  = [0, 0, 0, 0];  // índice 0=N1
    const detalle        = [];

    const pctArr = [pct.n1, pct.n2, pct.n3, pct.n4];
    
    // Agregar compras propias del titular al detalle (como nivel "0" o propias)
    if (comprasMap[titularId] && comprasMap[titularId].cajas > 0) {
        detalle.push({
            nivel: 0, // 0 = Propia
            nombre: `${titular.nombre} ${titular.apellido}`,
            id: titular.id,
            cajas: comprasMap[titularId].cajas,
            pct: 0, // No comisiona de si mismo
            comision: 0,
            pais: titular.pais,
            usoActivos: comprasMap[titularId].usoActivos
        });
    }

    for (let nivel = 0; nivel < 4; nivel++) {
        const nuevaFrontera = [];

        // Encontrar hijos de todos los del nivel anterior
        usuarios.forEach(u => {
            if (fronteraNivel.includes(u.patrocinador_id) && !visited.has(u.id)) {
                visited.add(u.id);
                nuevaFrontera.push(u.id);

                const cInfo = comprasMap[u.id];
                if (cInfo && cInfo.cajas > 0) {
                    const comision = cInfo.cajas * pBox * (pctArr[nivel] / 100);
                    porNivelMonto[nivel] += comision;
                    detalle.push({
                        nivel: nivel + 1,
                        nombre: `${u.nombre} ${u.apellido}`,
                        id: u.id,
                        cajas: cInfo.cajas,
                        pct: pctArr[nivel],
                        comision,
                        pais: u.pais,
                        usoActivos: cInfo.usoActivos
                    });
                }
            }
        });

        fronteraNivel = nuevaFrontera;
        if (!fronteraNivel.length) break;
    }

    // Comisión de la compra propia del titular NO genera activos
    // Los activos gastados = compras propias × cierto % (acá usamos un estimado)
    const propias = (comprasMap[titularId]?.cajas || 0) * pBox * 0.10;  // 10% de costo propio como "gastado"
    const monto_generado  = porNivelMonto.reduce((a,b) => a+b, 0);
    const monto_gastado   = Math.max(propias, monto_generado * 0.15);  // min 15%
    const monto_disponible = monto_generado - monto_gastado;

    return { por_nivel: porNivelMonto, detalle, monto_generado, monto_gastado, monto_disponible, moneda, pBox };
}

